import { db, generateId } from './FishingDB';
import { CatchLog, FishingSession } from '../entities/FishingEntities';

// Hämta nuvarande GPS-position
async function getCurrentPosition(): Promise<{ lat: number; lng: number } | null> {
  if (!navigator.geolocation) return null;

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      () => resolve(null),
      { timeout: 5000, enableHighAccuracy: true }
    );
  });
}

// === SUPER ENKEL API ===

// Logga fångst (eller kontakt om caught=false)
export async function logCatch(options: {
  species: string; // Species KEY
  count?: number;
  caught?: boolean;
  weight?: number; // in GRAMS
  length?: number; // in MILLIMETERS
  fishingDepth?: number; // in MILLIMETERS
  depthCategory?: 'shallow' | 'medium' | 'deep'; // Depth category for fishing score API
  method?: string; // Fishing method key
  tackle?: string[]; // Array of tackle keys
  notes?: string;
  waterTemp?: number; // User-provided water temp (overrides API estimate)
  photo?: string; // Base64 photo data
  customTimestamp?: number; // Custom timestamp for backlog
  sessionId?: string; // Explicit session ID (for backlog or manual selection)
  lat?: number; // Manual latitude override
  lng?: number; // Manual longitude override
  fishingAreaId?: string; // Manual area ID override
}): Promise<CatchLog[]> {
  const {
    species,
    count = 1,
    caught = true,
    weight,
    length,
    fishingDepth,
    depthCategory,
    method,
    tackle,
    notes,
    waterTemp: userWaterTemp,
    photo,
    customTimestamp,
    sessionId: explicitSessionId,
    lat: manualLat,
    lng: manualLng,
    fishingAreaId: manualAreaId
  } = options;

  // Hämta position (use manual override if provided)
  let position: { lat: number; lng: number } | null = null;
  if (manualLat !== undefined && manualLng !== undefined) {
    position = { lat: manualLat, lng: manualLng };
  } else if (manualLat === undefined && manualLng === undefined) {
    // Only fetch GPS if no manual override
    position = await getCurrentPosition();
  }
  // If only one is undefined, position remains null (invalid override)

  // Handle session (use explicit if provided, otherwise auto-create)
  let activeSession = null;
  if (explicitSessionId !== undefined) {
    // Use explicit session ID (could be undefined for "no session")
    if (explicitSessionId) {
      activeSession = await db.sessions.get(explicitSessionId);
    }
  } else {
    // Auto-create session if not specified and position exists
    activeSession = await getActiveSession();
    if (!activeSession && position) {
      activeSession = await startSession({ autoCreated: true });
      console.log('📝 Auto-created fishing session');
    }
  }

  // Base catch data (use custom timestamp if provided)
  const timestamp = customTimestamp || Date.now();
  
  // Fetch complete weather data from API
  let weatherData = null;
  if (position?.lat && position?.lng) {
    const { fetchWeatherForCatch } = await import('./WeatherForCatch');
    weatherData = await fetchWeatherForCatch(
      position.lat, 
      position.lng, 
      timestamp, // Use catch timestamp for historical weather
      species,
      depthCategory // Pass depth category to API for better fishing score
    );
    
    if (!weatherData) {
      // Queue for later if offline
      console.log('📋 Queued weather update for when online');
    }
  }

  // Determine water temperature with priority:
  // 1. User-provided waterTemp (from catch input)
  // 2. Session waterTemp (if exists)
  // 3. API estimated waterTemp (from weatherData)
  let finalWaterTemp: number | undefined = undefined;
  if (userWaterTemp !== undefined) {
    finalWaterTemp = userWaterTemp;
    console.log(`🌡️ Using user-provided water temp: ${finalWaterTemp}°C`);
  } else if (activeSession?.waterTemp !== undefined) {
    finalWaterTemp = activeSession.waterTemp;
    console.log(`🌡️ Using session water temp: ${finalWaterTemp}°C`);
  } else if (weatherData?.waterTemp !== undefined) {
    finalWaterTemp = weatherData.waterTemp;
    console.log(`🌡️ Using API estimated water temp: ${finalWaterTemp}°C`);
  }

  const baseCatch: Omit<CatchLog, 'id'> = {
    species,
    caught,
    timestamp,
    lat: position?.lat,
    lng: position?.lng,
    sessionId: activeSession?.id,
    weight,
    length,
    fishingDepth,
    depthCategory,
    method,
    tackle,
    notes,
    photo,
    // Weather data (all fields from API)
    ...(weatherData && {
      airTemp: weatherData.airTemp,
      feelsLike: weatherData.feelsLike,
      humidity: weatherData.humidity,
      pressure: weatherData.pressure,
      windSpeed: weatherData.windSpeed,
      windGust: weatherData.windGust,
      windDirection: weatherData.windDirection,
      precipitation: weatherData.precipitation,
      visibility: weatherData.visibility,
      weatherCode: weatherData.weatherCode,
      cloudiness: weatherData.cloudiness,
      moonPhase: weatherData.moonPhase,
      sunrise: weatherData.sunrise,
      sunset: weatherData.sunset,
      fishingScore: weatherData.fishingScore,
      fishingRating: weatherData.fishingRating,
      // Include 36h weather history for trend analysis
      weatherHistory: weatherData.weatherHistory
    }),
    // Use prioritized waterTemp (can override API value)
    waterTemp: finalWaterTemp
  };

  // Skapa 'count' antal fångster
  const catches: CatchLog[] = [];
  for (let i = 0; i < count; i++) {
    const newCatch: CatchLog = {
      ...baseCatch,
      id: generateId()
    };
    await db.catches.add(newCatch);
    catches.push(newCatch);
  }

  // Queue weather update if we were offline
  if (position?.lat && position?.lng && !weatherData) {
    const { queueWeatherUpdate } = await import('./WeatherForCatch');
    catches.forEach(c => queueWeatherUpdate(
      c.id, 
      position!.lat, 
      position!.lng, 
      c.timestamp, // Use catch timestamp for historical weather
      species,
      depthCategory // Pass depth category for better API call later
    ));
  }

  // Uppdatera session stats
  if (activeSession) {
    await updateSessionStats(activeSession.id);
  }

  console.log(`✅ Logged ${count} ${species} (${caught ? 'landad 🎣' : 'kontakt 💨'})`);

  // Auto-skapa eller uppdatera fishing area om vi har position
  // Use manual area ID if provided, otherwise auto-assign
  if (manualAreaId) {
    // Manual area override: assign directly
    for (const catchLog of catches) {
      await db.catches.update(catchLog.id!, { fishingAreaId: manualAreaId });
    }
    console.log(`📍 Assigned to manual area: ${manualAreaId}`);
  } else if (position) {
    // Auto-assign based on position
    await assignOrCreateFishingArea(catches);
  }

  return catches;
}

// Starta fiskepass
export async function startSession(options?: {
  waterTemp?: number;
  notes?: string;
  autoCreated?: boolean; // Flag for silently auto-created sessions
}): Promise<FishingSession> {
  // Stoppa aktiv session först
  const active = await getActiveSession();
  if (active) {
    console.log('🛑 Ending active session before starting new:', active.id);
    await endSession(active.id);
  } else {
    console.log('✅ No active session to end');
  }

  const position = await getCurrentPosition();

  const session: FishingSession = {
    id: generateId(),
    startTime: Date.now(),
    isActive: true,
    lat: position?.lat,
    lng: position?.lng,
    waterTemp: options?.waterTemp,
    notes: options?.notes,
    catchCount: 0,
    totalWeight: 0
  };

  await db.sessions.add(session);

  if (!options?.autoCreated) {
    console.log('✅ Session started! 🎣');
  } else {
    console.log('🤖 Auto-created session:', session.id);
  }

  return session;
}

// Avsluta fiskepass
export async function endSession(sessionId?: string): Promise<void> {
  const session = sessionId
    ? await db.sessions.get(sessionId)
    : await getActiveSession();

  if (!session) {
    console.log('⚠️ No session to end');
    return;
  }

  console.log('🏁 Ending session:', session.id);
  await db.sessions.update(session.id, {
    endTime: Date.now(),
    isActive: false
  });

  console.log('✅ Session ended:', session.id);
}

// Hämta aktiv session
export async function getActiveSession(): Promise<FishingSession | null> {
  // Get all sessions and filter manually since isActive is boolean
  const allSessions = await db.sessions.toArray();
  const activeSessions = allSessions
    .filter(s => s.isActive === true && !s.endTime)
    .sort((a, b) => b.startTime - a.startTime); // Most recent first
  
  const active = activeSessions[0] || null;
  if (active) {
    console.log('🔍 Found active session:', active.id, 'isActive:', active.isActive, 'endTime:', active.endTime);
  } else {
    console.log('🔍 No active session found');
  }
  return active;
}

// Uppdatera session-statistik
async function updateSessionStats(sessionId: string): Promise<void> {
  const catches = await db.catches
    .where('sessionId')
    .equals(sessionId)
    .and(c => c.caught === true) // Räkna bara landade fiskar
    .toArray();

  const catchCount = catches.length;
  const totalWeight = catches.reduce((sum: number, c: CatchLog) => sum + (c.weight || 0), 0);

  await db.sessions.update(sessionId, {
    catchCount,
    totalWeight
  });
}

// Hämta senaste fångster
export async function getRecentCatches(limit: number = 50): Promise<CatchLog[]> {
  return await db.catches
    .orderBy('timestamp')
    .reverse()
    .limit(limit)
    .toArray();
}

// Hämta fångster för session
export async function getSessionCatches(sessionId: string): Promise<CatchLog[]> {
  return await db.catches
    .where('sessionId')
    .equals(sessionId)
    .reverse()
    .sortBy('timestamp');
}

// Hämta alla sessioner
export async function getAllSessions(): Promise<FishingSession[]> {
  return await db.sessions
    .orderBy('startTime')
    .reverse()
    .toArray();
}

// Uppdatera fångst (lägg till vikt/längd/foto senare)
export async function updateCatch(
  catchId: string,
  updates: Partial<CatchLog>
): Promise<void> {
  await db.catches.update(catchId, updates);
  console.log('✅ Catch updated!');
}

// Ta bort fångst
export async function deleteCatch(catchId: string): Promise<void> {
  const catchData = await db.catches.get(catchId);
  if (!catchData) return;

  await db.catches.delete(catchId);

  // Uppdatera session stats
  if (catchData.sessionId) {
    await updateSessionStats(catchData.sessionId);
  }

  console.log('✅ Catch deleted!');
}

// === FISHING AREA MANAGEMENT ===

// Beräkna avstånd mellan två GPS-koordinater (Haversine formula)
function calculateDistance(
  lat1: number, lng1: number,
  lat2: number, lng2: number
): number {
  const R = 6371e3; // Earth radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lng2 - lng1) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) *
    Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

// Auto-assign catches to existing area or create new one
async function assignOrCreateFishingArea(catches: CatchLog[]): Promise<void> {
  const AREA_RADIUS = 150; // meters

  for (const catchLog of catches) {
    if (!catchLog.lat || !catchLog.lng) continue;

    // Hitta alla existerande områden
    const allAreas = await db.areas.toArray();

    // Sök efter närliggande område
    let closestArea = null;
    let closestDistance = Infinity;

    for (const area of allAreas) {
      const distance = calculateDistance(
        catchLog.lat, catchLog.lng,
        area.centerLat, area.centerLng
      );

      if (distance < AREA_RADIUS && distance < closestDistance) {
        closestArea = area;
        closestDistance = distance;
      }
    }

    if (closestArea) {
      // Uppdatera befintligt område
      await db.catches.update(catchLog.id, {
        fishingAreaId: closestArea.id
      });

      // Uppdatera area stats
      await updateAreaStats(closestArea.id);
    } else {
      // Skapa nytt område med platsnamn från backend API (eller kö om offline)
      const { getLocationName, queueLocationUpdate } = await import('./GeocodingService');
      
      let locationName = 'Fiskeområde'; // Temporary fallback
      let needsQueue = false;
      
      try {
        locationName = await getLocationName(catchLog.lat, catchLog.lng);
      } catch (error) {
        console.warn('Failed to get location name (offline?), will queue for later');
        needsQueue = true;
        locationName = `Fiskeområde #${allAreas.length + 1}`; // Temporary name
      }
      
      const newArea = {
        id: generateId(),
        name: locationName,
        centerLat: catchLog.lat,
        centerLng: catchLog.lng,
        radius: AREA_RADIUS,
        totalCatches: 1,
        lastVisited: Date.now()
      };

      await db.areas.add(newArea);

      // Queue location update if we were offline
      if (needsQueue) {
        await queueLocationUpdate(newArea.id, catchLog.lat, catchLog.lng);
      }

      // Koppla fångsten till det nya området
      await db.catches.update(catchLog.id, {
        fishingAreaId: newArea.id
      });

      console.log(`📍 Created new fishing area: ${newArea.name}${needsQueue ? ' (queued for name update)' : ''}`);
    }
  }
}

// Uppdatera statistik för ett område
async function updateAreaStats(areaId: string): Promise<void> {
  const catches = await db.catches
    .where('fishingAreaId')
    .equals(areaId)
    .toArray();

  const lastCatch = catches.length > 0
    ? Math.max(...catches.map(c => c.timestamp))
    : Date.now();

  await db.areas.update(areaId, {
    totalCatches: catches.length,
    lastVisited: lastCatch
  });
}

// Hämta alla områden
export async function getAllAreas() {
  return await db.areas.orderBy('lastVisited').reverse().toArray();
}

// Uppdatera area namn
export async function updateAreaName(areaId: string, name: string): Promise<void> {
  await db.areas.update(areaId, { name });
  console.log(`📍 Area renamed to: ${name}`);
}

// Ta bort area (flyttar fångster till "ingen area")
export async function deleteArea(areaId: string): Promise<void> {
  // Ta bort area-kopplingen från alla fångster
  const catches = await db.catches
    .where('fishingAreaId')
    .equals(areaId)
    .toArray();

  for (const catchLog of catches) {
    await db.catches.update(catchLog.id, { fishingAreaId: undefined });
  }

  await db.areas.delete(areaId);
  console.log('📍 Area deleted');
}
